import { useState, useMemo } from 'react';
import { Input } from '~/components/ui/input';
import { Checkbox } from '~/components/ui/checkbox';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import type { WizardFamilyWithStatus } from '../types';

interface SelectFamiliesStepProps {
  families: WizardFamilyWithStatus[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

function SelectFamiliesStep({ families, selectedIds, onSelectionChange }: SelectFamiliesStepProps) {
  const [search, setSearch] = useState('');

  const filteredFamilies = useMemo(() => {
    if (!search) return families;
    const searchLower = search.toLowerCase();
    return families.filter(
      f =>
        f.name.toLowerCase().includes(searchLower) ||
        (f.primaryContactEmail && f.primaryContactEmail.toLowerCase().includes(searchLower))
    );
  }, [families, search]);

  const handleToggleAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange(filteredFamilies.map(f => f.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleToggleFamily = (familyId: string) => {
    if (selectedIds.includes(familyId)) {
      onSelectionChange(selectedIds.filter(id => id !== familyId));
    } else {
      onSelectionChange([...selectedIds, familyId]);
    }
  };

  const allSelected = filteredFamilies.length > 0 &&
    filteredFamilies.every(f => selectedIds.includes(f.id));
  const someSelected = filteredFamilies.some(f => selectedIds.includes(f.id));

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Select the families to include in this enrollment batch. Families with existing
        enrollments are shown but can still be selected to add more students.
      </p>

      <div className="flex items-center gap-4">
        <Input
          placeholder="Search families..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <div className="text-sm text-muted-foreground">
          {selectedIds.length} of {families.length} selected
        </div>
      </div>

      <div className="rounded-md border max-h-96 overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={allSelected || (someSelected && 'indeterminate')}
                  onCheckedChange={handleToggleAll}
                  aria-label="Select all"
                />
              </TableHead>
              <TableHead>Family</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Children</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredFamilies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No families found
                </TableCell>
              </TableRow>
            ) : (
              filteredFamilies.map(family => (
                <TableRow
                  key={family.id}
                  className={family.hasExistingEnrollment ? 'bg-muted/50' : ''}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.includes(family.id)}
                      onCheckedChange={() => handleToggleFamily(family.id)}
                      aria-label={`Select ${family.name}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{family.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {family.primaryContactEmail || '-'}
                  </TableCell>
                  <TableCell>{family.children?.length || 0}</TableCell>
                  <TableCell>
                    {family.hasExistingEnrollment ? (
                      <Badge variant="outline">Has Enrollment</Badge>
                    ) : (
                      <Badge variant="secondary">New</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleToggleAll(true)}
        >
          Select All
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleToggleAll(false)}
        >
          Clear Selection
        </Button>
      </div>
    </div>
  );
}

export default SelectFamiliesStep;
